/* =============================================================================
 * terrain-generator.js  —  Main-thread UI (ES module, reduced scope)
 * -----------------------------------------------------------------------------
 * Customer controls (only three):
 *   1) GPX upload  — defines the terrain area (bbox) and the route drawn on it
 *   2) Vertical exaggeration — live slider (rebuilds vertex Z from cached grid)
 *   3) Colors — terrain + route color pickers (update materials live)
 * Merchant-fixed via {% schema %}: detail, model size, base thickness, footprint.
 *
 * The worker returns a heightfield once; all geometry is built here so the
 * exaggeration slider is live with no re-fetch.
 * ===========================================================================*/

import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';

document.querySelectorAll('[data-terrain-generator]').forEach(initInstance);

function initInstance(root) {
  const cfg = JSON.parse(root.querySelector('[data-tg-config]').textContent);
  const $ = (s) => root.querySelector(s);

  const els = {
    gpx: $('[data-tg-gpx]'),
    gpxInfo: $('[data-tg-gpxinfo]'),
    generate: $('[data-tg-generate]'),
    addToCart: $('[data-tg-addcart]'),
    progressWrap: $('[data-tg-progress]'),
    progressBar: $('[data-tg-progressbar]'),
    progressMsg: $('[data-tg-progressmsg]'),
    viewer: $('[data-tg-viewer]'),
    status: $('[data-tg-status]'),
    stats: $('[data-tg-stats]'),
    controls: $('[data-tg-controls]'),
    vscale: $('[name="tg_vscale"]'),
    vscaleEcho: $('[data-tg-echo="vscale"]'),
    colorTerrain: $('[name="tg_color_terrain"]'),
    colorRoute: $('[name="tg_color_route"]'),
    mapEl: $('[data-tg-map]'),
    area: $('[name="tg_area"]'),
    areaEcho: $('[data-tg-echo="area"]'),
    personalizer: $('[data-tg-personalizer]'),
    overlay: $('[data-tg-overlay]'),
    nfcUrl: $('[data-tg-nfc-url]'),
    nfcMsg: $('[data-tg-nfc-msg]'),
    ovText: $('[data-tg-overlay-text]'),
    ovSub: $('[data-tg-overlay-sub]'),
    ovProgress: $('[data-tg-overlay-progress]'),
    ovBar: $('[data-tg-overlay-bar]'),
    ovPct: $('[data-tg-overlay-pct]'),
    extras: $('[data-tg-extras]'),
    roadColorWrap: $('[data-tg-roadcolor]'),
    colorRoad: $('[name="tg_color_road"]'),
  };
  // Személyre szabható mezők: kulcs + input + mely kivitelnél látszik
  const fieldDefs = Array.prototype.slice.call(root.querySelectorAll('[data-tg-field]')).map((inp) => {
    const lab = inp.closest('label'); const span = lab ? lab.querySelector('span') : null;
    const forEl = inp.closest('[data-tg-for]');
    return {
      key: inp.getAttribute('data-tg-field'),
      input: inp,
      label: span ? span.textContent.trim() : inp.getAttribute('data-tg-field'),
      forVariants: (forEl ? forEl.getAttribute('data-tg-for') : '').split(/\s+/),
      wrap: forEl,
    };
  });

  // Fixed defaults come from the section schema (merchant-set, not customer-facing).
  const FIXED = {
    detail: cfg.fixed.detail,
    sizeMM: cfg.fixed.sizeMM,
    baseMM: cfg.fixed.baseMM,
    footprint: cfg.fixed.footprint,
    routeWidthMM: cfg.fixed.routeWidthMM,
    routeRiseMM: cfg.fixed.routeRiseMM,
  };

  const state = {
    worker: null, three: null,
    track: null, gpxFile: null, bbox: null,
    height: null, routeUV: null,     // cached heightfield + normalized route
    meshTerrain: null, meshRoute: null, group: null,
    lastStats: null, busy: false, rafPending: false,
    trackBbox: null, squareBbox: null, map: null, mapLine: null, mapOutline: null,
    _minSide: 0, _center: null,
    selectedVariant: null, selectedKivitel: null, selectedType: null,
  };

  els.colorTerrain.value = cfg.colorTerrain;
  els.colorRoute.value = cfg.colorRoute;
  els.vscale.value = cfg.defaultVscale;
  els.vscaleEcho.textContent = cfg.defaultVscale;
  setStatus('Tölts fel egy GPX fájlt a kezdéshez.');
  els.controls.hidden = false; // a lépés vezérli a láthatóságát
  els.addToCart.disabled = true;

  /* -------------------------------------------------- lépés-navigáció (wizard) */
  const steps = {};
  root.querySelectorAll('[data-tg-step]').forEach((el) => { steps[el.getAttribute('data-tg-step')] = el; });
  const toolAnchor = (root.closest('.rt') || document).querySelector('[data-tg-anchor]') || root;
  function scrollToTool() {
    try { toolAnchor.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e) { toolAnchor.scrollIntoView(); }
  }
  function goStep(name) {
    if (name === 'upload') resetAll();     // a GPX-lépéshez visszaérve mindent törlünk
    Object.keys(steps).forEach((k) => { steps[k].hidden = (k !== name); });
    if (name === 'map' && state.map) setTimeout(() => { try { state.map.invalidateSize(); if (state.mapOutline) state.map.fitBounds(state.mapOutline.getBounds().pad(0.15)); } catch (e) {} }, 60);
    if (state._ready) scrollToTool();      // művelet/lépésváltáskor ugorjunk „A tervező"-höz (kezdő betöltéskor nem)
  }

  // Teljes visszaállítás — minden korábbi feltöltés/generálás nyoma törlődik.
  function resetAll() {
    if (els.gpx) els.gpx.value = '';                 // fontos: enged újat feltölteni (akár ugyanazt is)
    if (els.gpxInfo) els.gpxInfo.textContent = '';
    state.track = state.gpxFile = state.bbox = state.trackBbox = state.squareBbox = null;
    state.height = state.routeUV = state.lastStats = state.water = null;
    state.selectedVariant = state.selectedKivitel = state.selectedType = state.selectedSize = null;
    root.querySelectorAll('[data-tg-sizes]').forEach((w) => { w.hidden = true; w.querySelectorAll('.tg__size').forEach((b) => b.classList.remove('is-selected')); });
    if (state.mapOutline) { try { state.mapOutline.remove(); } catch (e) {} state.mapOutline = null; }
    if (state.mapLine) { try { state.mapLine.remove(); } catch (e) {} state.mapLine = null; }
    if (state.map) { try { state.map.remove(); } catch (e) {} state.map = null; }
    if (state.group && state.three) { try { disposeGroup(state.group); state.three.scene.remove(state.group); } catch (e) {} state.group = null; state.meshTerrain = state.meshRoute = state.meshWater = state.meshRivers = state.meshRoads = null; }
    if (typeof fieldDefs !== 'undefined') fieldDefs.forEach((f) => { f.input.value = ''; });
    [tH, tM, tS].forEach((el) => { if (el) el.value = ''; });
    root.querySelectorAll('[data-tg-modelimg]').forEach((img) => { img.removeAttribute('src'); });
    root.querySelectorAll('[data-tg-option]').forEach((o) => o.classList.remove('is-selected'));
    if (els.personalizer) els.personalizer.hidden = true;
    if (els.generate) els.generate.disabled = true;
    if (els.addToCart) els.addToCart.disabled = true;
    showProgress(false);
    setStatus('Tölts fel egy GPX fájlt a kezdéshez.');
  }

  root.querySelectorAll('[data-tg-back]').forEach((btn) => {
    btn.addEventListener('click', () => goStep(btn.getAttribute('data-tg-back')));
  });
  root.querySelectorAll('[data-tg-next]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const t = btn.getAttribute('data-tg-next');
      if (t === 'product') { captureModel(); autofillFields(); }
      goStep(t);
    });
  });

  // --- élő előnézet + személyre szabás (4. lépés) ---
  function captureModel() {
    if (!state.three || !state.group) return;
    try {
      const three = state.three, r = three.renderer;
      const box = new THREE.Box3().setFromObject(state.group);
      const size = box.getSize(new THREE.Vector3()), c = box.getCenter(new THREE.Vector3());
      const half = Math.max(size.x, size.z) / 2 * 1.002;   // pontosan a modell alapja
      const H = Math.max(size.y, half);
      // ORTOGRAFIKUS (párhuzamos) felülnézet: a körvonal pontosan a 10x10 cm-es alap.
      const cam = new THREE.OrthographicCamera(-half, half, half, -half, 0.01, H * 6 + 10);
      cam.up.set(0, 0, -1);
      cam.position.set(c.x, box.max.y + H * 2, c.z);
      cam.lookAt(c.x, c.y, c.z);
      cam.updateProjectionMatrix();
      // TÚLMINTAVÉTELEZÉS: 1600x1600-ban veszünk fel, a megjelenítés ennek a töredéke
      // -> a böngésző lekicsinyíti, így nem lesz pixeles/recés a kép.
      const S = 1600;
      const prevPR = r.getPixelRatio();
      r.setPixelRatio(1);                 // a puffer pontosan 1600x1600 (a 2048-as korlát alatt)
      r.setSize(S, S, false);
      // a felvétel idejére sima árnyékolás, hogy a rács facettái ne látszódjanak "kockásnak"
      const flatMats = [];
      [state.meshTerrain, state.meshRoute].forEach((m) => {
        if (m && m.material && m.material.flatShading) { flatMats.push(m.material); m.material.flatShading = false; m.material.needsUpdate = true; }
      });
      r.render(three.scene, cam);
      const url = r.domElement.toDataURL('image/png');
      flatMats.forEach((mt) => { mt.flatShading = true; mt.needsUpdate = true; });
      r.setPixelRatio(prevPR);
      if (three.onResize) three.onResize();   // az élő 3D nézet visszaállítása
      root.querySelectorAll('[data-tg-modelimg]').forEach((img) => { img.src = url; });
    } catch (e) {}
  }
  function bindKey(key, val) {
    root.querySelectorAll('[data-tg-bind="' + key + '"]').forEach((el) => { el.textContent = val; });
  }
  function syncBinds() { fieldDefs.forEach((f) => bindKey(f.key, f.input.value)); renderPlate(); }
  function autofillFields() {
    fieldDefs.forEach((f) => {
      if (f.input.value) return;
      if (f.key === 'distance' && state.track) f.input.value = estimateKm(state.track).toFixed(2) + ' km';
      else if (f.key === 'elevation' && state.track) { const a = trackAscent(state.track); const v = (a != null) ? a : (state.lastStats ? state.lastStats.reliefMetres : null); if (v != null) f.input.value = v + ' m'; }
      else if (f.key === 'date' && state.track) { const d = trackDate(state.track); if (d) f.input.value = d; }
      else if (f.key === 'title' && state.gpxFile) f.input.value = state.gpxFile.name.replace(/\.gpx$/i, '').replace(/[_-]+/g, ' ').trim().toUpperCase();
      // Idő: szándékosan NEM töltjük automatikusan (a GPX időbélyeg gyakran pontatlan) — a vásárló írja be.
    });
    syncBinds();
  }
  fieldDefs.forEach((f) => f.input.addEventListener('input', () => { bindKey(f.key, f.input.value); renderPlate(); }));

  /* --- Méret (variáns) választás: a termék variánsaiból generálva, bővítéskor automatikusan --- */
  // Az előnézet + személyre szabó mezők megnyitása (a kiválasztott kivitelhez)
  function openPersonalizer() {
    if (els.personalizer) els.personalizer.hidden = false;
    fieldDefs.forEach((f) => { if (f.wrap) f.wrap.hidden = (f.forVariants.indexOf(state.selectedType) === -1); });
    root.querySelectorAll('[data-tg-preview]').forEach((p) => {
      p.hidden = (p.getAttribute('data-tg-preview') !== state.selectedType);
    });
    showSlots(state.selectedType === 'stand');
    syncBinds();
  }
  function hidePersonalizer() { if (els.personalizer) els.personalizer.hidden = true; }

  function selectSize(btn) {
    var wrap = btn.closest('[data-tg-sizes]');
    if (wrap) wrap.querySelectorAll('.tg__size').forEach(function (b) { b.classList.toggle('is-selected', b === btn); });
    state.selectedVariant = btn.getAttribute('data-variant') || '';
    state.selectedSize = btn.getAttribute('data-title') || '';
    openPersonalizer();                     // a méret megvan -> jöhet az előnézet
    if (els.addToCart) els.addToCart.disabled = !state.selectedVariant;
  }
  function showSizes(kind) {
    var auto = null, count = 0;
    root.querySelectorAll('[data-tg-sizes]').forEach(function (w) {
      var on = w.getAttribute('data-tg-sizes') === kind;
      w.hidden = !on;
      w.querySelectorAll('.tg__size').forEach(function (b) {
        b.classList.remove('is-selected');
        if (on && !b.disabled) { count++; if (!auto) auto = b; }
      });
    });
    state.selectedVariant = ''; state.selectedSize = '';
    hidePersonalizer();                             // amíg nincs méret, nincs előnézet sem
    if (count === 1 && auto) selectSize(auto);      // ha csak egy méret van, automatikusan azt választjuk
    else if (els.addToCart) els.addToCart.disabled = true;
  }
  root.querySelectorAll('[data-tg-sizes] .tg__size').forEach(function (b) {
    b.addEventListener('click', function () { if (!b.disabled) selectSize(b); });
  });

  /* --- Állvány névtábla: melyik adat melyik sarokba kerüljön (4 hely) --- */
  var SLOT_LABELS = { tl: 'Bal fent', tr: 'Jobb fent', bl: 'Bal alul', br: 'Jobb alul' };
  var slotSelects = Array.prototype.slice.call(root.querySelectorAll('[data-tg-slot]'));
  function fieldByKey(k) { for (var i = 0; i < fieldDefs.length; i++) if (fieldDefs[i].key === k) return fieldDefs[i]; return null; }
  function plateValues() {
    var used = {}, v = { tl: '', tr: '', bl: '', br: '' };
    slotSelects.forEach(function (sel) { if (sel.value) used[sel.value] = sel.getAttribute('data-tg-slot'); });
    ['tl', 'tr', 'bl', 'br'].forEach(function (slot) {
      var f = used[slot] ? fieldByKey(used[slot]) : null;
      v[slot] = f ? (f.input.value || '').trim() : '';
    });
    v._key = used;
    return v;
  }
  // A névtábla elrendezése AUTOMATIKUS:
  //   1 adat összesen           -> a tábla abszolút közepén
  //   egy SORBAN 1 adat          -> az az adat a saját sorában vízszintesen középen
  //                                (soronként külön dől el, a másik sortól függetlenül)
  //   ha egyik sorban sincs 1    -> és minden OSZLOP-ban 1 adat van, akkor függőlegesen középen
  //   egyébként (pl. 4 adat)     -> a négy sarokban
  function plateLayout() {
    var v = plateValues();
    var n = { tl: v.tl ? 1 : 0, tr: v.tr ? 1 : 0, bl: v.bl ? 1 : 0, br: v.br ? 1 : 0 };
    var total = n.tl + n.tr + n.bl + n.br;
    var rows = { top: n.tl + n.tr, bottom: n.bl + n.br };
    var cols = [n.tl + n.bl, n.tr + n.br];
    var anyRowOne = rows.top === 1 || rows.bottom === 1;
    var everyColOne = cols.filter(function (c) { return c > 0; }).every(function (c) { return c === 1; });
    var colMode = !anyRowOne && everyColOne;
    return { v: v, total: total, rows: rows, colMode: colMode, full: total === 1 };
  }
  function slotRow(slot) { return (slot === 'tl' || slot === 'tr') ? 'top' : 'bottom'; }
  function renderPlate() {
    if (!slotSelects) return;
    var L = plateLayout(), v = L.v;
    ['tl', 'tr', 'bl', 'br'].forEach(function (slot) {
      root.querySelectorAll('[data-tg-cell="' + slot + '"]').forEach(function (el) {
        el.textContent = v[slot];
        el.classList.remove('is-row-center', 'is-col-center', 'is-full-center');
        if (!v[slot]) return;
        if (L.full) el.classList.add('is-full-center');
        else if (L.rows[slotRow(slot)] === 1) el.classList.add('is-row-center');
        else if (L.colMode) el.classList.add('is-col-center');
      });
    });
  }
  function plateLayoutText() {
    var L = plateLayout(), v = L.v;
    if (L.total === 0) return '';
    if (L.full) return 'Középen: ' + (v.tl || v.tr || v.bl || v.br);
    var out = [];
    if (L.colMode) {
      if (v.tl || v.bl) out.push('Bal oldal középen: ' + (v.tl || v.bl));
      if (v.tr || v.br) out.push('Jobb oldal középen: ' + (v.tr || v.br));
      return out.join(' · ');
    }
    if (L.rows.top === 1) out.push('Felső sor középen: ' + (v.tl || v.tr));
    else { if (v.tl) out.push('Bal fent: ' + v.tl); if (v.tr) out.push('Jobb fent: ' + v.tr); }
    if (L.rows.bottom === 1) out.push('Alsó sor középen: ' + (v.bl || v.br));
    else { if (v.bl) out.push('Bal alul: ' + v.bl); if (v.br) out.push('Jobb alul: ' + v.br); }
    return out.join(' · ');
  }
  function showSlots(on) { root.querySelectorAll('[data-tg-slot-wrap]').forEach(function (el) { el.hidden = !on; }); }
  slotSelects.forEach(function (sel) {
    sel.addEventListener('change', function () {
      // egy helyre csak egy adat kerülhet
      if (sel.value) slotSelects.forEach(function (o) { if (o !== sel && o.value === sel.value) o.value = ''; });
      renderPlate();
    });
  });

  // Idő: óra (0–23) : perc (0–59) : mp (0–59) → egyesített "H:MM:SS"
  const tH = root.querySelector('[data-tg-time-h]'), tM = root.querySelector('[data-tg-time-m]'), tS = root.querySelector('[data-tg-time-s]');
  const tHidden = root.querySelector('[data-tg-time] [data-tg-field="time"]');
  function clampField(el, max) {
    if (!el) return null;
    let v = parseInt(el.value, 10);
    if (isNaN(v)) { el.value = ''; return null; }
    if (v < 0) v = 0; if (v > max) v = max;
    el.value = String(v);
    return v;
  }
  function updateTime() {
    const h = clampField(tH, 23), m = clampField(tM, 59), s = clampField(tS, 59);
    let val = '';
    if (h != null || m != null || s != null) val = (h || 0) + ':' + String(m || 0).padStart(2, '0') + ':' + String(s || 0).padStart(2, '0');
    if (tHidden) tHidden.value = val;
    bindKey('time', val);
    renderPlate();
  }
  [tH, tM, tS].forEach((el) => { if (el) el.addEventListener('input', updateTime); });

  const optionEls = Array.prototype.slice.call(root.querySelectorAll('[data-tg-option]'));
  optionEls.forEach((opt) => {
    opt.addEventListener('click', () => {
      if (opt.disabled) return;
      optionEls.forEach((o) => o.classList.toggle('is-selected', o === opt));
      state.selectedKivitel = opt.getAttribute('data-kivitel') || '';
      state.selectedType = opt.getAttribute('data-tg-option') || '';
      // Az előnézet és a személyre szabás CSAK méretválasztás után jelenik meg.
      showSizes(state.selectedType);
      els.addToCart.disabled = !state.selectedVariant;
    });
  });
  // A teljes oldalas maszkot a body-ra emeljük, így biztosan az egész oldalt takarja
  // (transform/overflow-hidden szülő különben "beszorítaná" a fixed pozíciót).
  if (els.overlay && els.overlay.parentElement !== document.body) document.body.appendChild(els.overlay);
  let overlayScroll = '';
  // opts: { title, sub, progress }  — progress:true esetén folyamatsáv és százalék is látszik
  function showOverlay(on, opts) {
    if (!els.overlay) return;
    const o = opts || {};
    if (on) {
      if (els.ovText && o.title) els.ovText.textContent = o.title;
      if (els.ovSub) els.ovSub.textContent = o.sub || '';
      if (els.ovProgress) els.ovProgress.hidden = !o.progress;
      if (o.progress) setOverlayProgress(0, '');
    }
    els.overlay.hidden = !on;
    if (on) { overlayScroll = document.body.style.overflow; document.body.style.overflow = 'hidden'; }
    else { document.body.style.overflow = overlayScroll; }
  }
  function setOverlayProgress(pct, msg) {
    const p = Math.max(0, Math.min(100, Math.round(pct || 0)));
    if (els.ovBar) { els.ovBar.style.width = p + '%'; els.ovBar.setAttribute('aria-valuenow', String(p)); }
    if (els.ovPct) els.ovPct.textContent = p + '%';
    if (msg && els.ovSub) els.ovSub.textContent = msg;
  }

  goStep('upload');
  state._ready = true;

  /* ------------------------------------------------------------- GPX upload */
  els.gpx.addEventListener('change', async (e) => {
    const file = e.target.files[0]; if (!file) return;
    if (file.size > 20 * 1024 * 1024) { setStatus('A GPX fájl nagyobb 20 MB-nál.', true); return; }
    try {
      const { track, bbox } = parseGpx(await file.text());
      if (track.length < 2) throw new Error('legalább két nyompont szükséges');
      state.track = track; state.gpxFile = file; state.trackBbox = bbox;
      const padX = (bbox[2] - bbox[0]) * 0.12 || 0.005, padY = (bbox[3] - bbox[1]) * 0.12 || 0.005;
      state.bbox = [bbox[0] - padX, bbox[1] - padY, bbox[2] + padX, bbox[3] + padY];
      const hasEle = track.some((p) => p.ele != null);
      els.gpxInfo.textContent =
        `${file.name} · ${track.length.toLocaleString()} pont · ${estimateKm(track).toFixed(1)} km` +
        (hasEle ? '' : ' · nincs pontonkénti magasság (a terep magassága az AWS csempékből jön)');
      track_event('tervezo_gpx_feltoltes', {
        pontok: track.length,
        tura_km: +estimateKm(track).toFixed(1),
        van_magassag: hasEle,
      });
      showMapStep();
    } catch (err) { setStatus('A GPX nem olvasható: ' + err.message, true); }
  });

  function parseGpx(xml) {
    const doc = new DOMParser().parseFromString(xml, 'application/xml');
    if (doc.querySelector('parsererror')) throw new Error('érvénytelen XML');
    let pts = doc.getElementsByTagName('trkpt');
    if (!pts.length) pts = doc.getElementsByTagName('rtept');
    const track = []; let w = Infinity, s = Infinity, e = -Infinity, n = -Infinity;
    for (const p of pts) {
      const lat = parseFloat(p.getAttribute('lat')), lon = parseFloat(p.getAttribute('lon'));
      if (!isFinite(lat) || !isFinite(lon)) continue;
      const eleEl = p.getElementsByTagName('ele')[0];
      const timeEl = p.getElementsByTagName('time')[0];
      track.push({ lat, lon, ele: eleEl ? parseFloat(eleEl.textContent) : null, time: timeEl ? timeEl.textContent : null });
      w = Math.min(w, lon); e = Math.max(e, lon); s = Math.min(s, lat); n = Math.max(n, lat);
    }
    return { track, bbox: [w, s, e, n] };
  }
  function estimateKm(track) {
    let km = 0;
    for (let i = 1; i < track.length; i++) {
      const a = track[i - 1], b = track[i];
      const dLat = (b.lat - a.lat) * 110.54;
      const dLon = (b.lon - a.lon) * 111.32 * Math.cos((a.lat + b.lat) / 2 * Math.PI / 180);
      km += Math.hypot(dLat, dLon);
    }
    return km;
  }
  function trackDate(track) {
    const t = track.find((p) => p.time); if (!t) return '';
    const d = new Date(t.time); if (isNaN(d.getTime())) return '';
    return d.getFullYear() + '.' + String(d.getMonth() + 1).padStart(2, '0') + '.' + String(d.getDate()).padStart(2, '0') + '.';
  }
  function trackDuration(track) {
    let a = null, b = null;
    for (const p of track) { if (p.time) { if (!a) a = p.time; b = p.time; } }
    if (!a || !b) return '';
    const s = (new Date(b).getTime() - new Date(a).getTime()) / 1000;
    if (!isFinite(s) || s <= 0) return '';
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = Math.floor(s % 60);
    return h + ':' + String(m).padStart(2, '0') + ':' + String(sec).padStart(2, '0');
  }
  function trackAscent(track) {
    let asc = 0, has = false, prev = null;
    for (const p of track) { if (p.ele == null) continue; has = true; if (prev != null) { const d = p.ele - prev; if (d > 0.5) asc += d; } prev = p.ele; }
    return has ? Math.round(asc) : null;
  }

  /* ------------------------------------------------- terület kijelölő térkép */
  const METRES_PER_DEG_LAT = 110540;
  const mPerDegLon = (lat) => 111320 * Math.cos(lat * Math.PI / 180);

  function trackGroundDims() {
    const [w, s, e, n] = state.trackBbox;
    const cLat = (s + n) / 2;
    return { gw: (e - w) * mPerDegLon(cLat), gh: (n - s) * METRES_PER_DEG_LAT, cLat, cLon: (w + e) / 2 };
  }

  // A modell VALÓDI alakja (kerekített négyzet, r = 0.18) lat/lon pontokként.
  function roundedSquareLatLngs(cLat, cLon, side, rFrac, segs) {
    const half = side / 2, r = side * rFrac, k = half - r, mLon = mPerDegLon(cLat);
    const P = (x, y) => [cLat + y / METRES_PER_DEG_LAT, cLon + x / mLon]; // x kelet, y észak (m)
    const pts = [];
    const arc = (cx, cy, a0, a1) => { for (let i = 0; i <= segs; i++) { const a = a0 + (a1 - a0) * i / segs; pts.push(P(cx + r * Math.cos(a), cy + r * Math.sin(a))); } };
    arc(k, k, 0, Math.PI / 2);                 // jobb-fent
    arc(-k, k, Math.PI / 2, Math.PI);          // bal-fent
    arc(-k, -k, Math.PI, 1.5 * Math.PI);       // bal-lent
    arc(k, -k, 1.5 * Math.PI, 2 * Math.PI);    // jobb-lent
    return pts;
  }

  function initMap() {
    if (state.map || !window.L || !els.mapEl) return;
    state.map = window.L.map(els.mapEl, { zoomControl: true, scrollWheelZoom: true });
    window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      { maxZoom: 19, attribution: '© OpenStreetMap' }).addTo(state.map);
  }

  function sliderSide() {
    const v = parseFloat(els.area && els.area.value) || 0;
    return state._minSide * (1 + (v / 100) * 1.4); // 1× … 2,4× (a régi 70% = az új 100%)
  }

  function updateOutline() {
    if (!state.map || !state._center) return;
    const side = sliderSide();
    const [cLat, cLon] = state._center;
    const latlngs = roundedSquareLatLngs(cLat, cLon, side, 0.18, 14);
    if (state.mapOutline) state.mapOutline.setLatLngs(latlngs);
    else state.mapOutline = window.L.polygon(latlngs,
      { color: '#FCFCFC', weight: 2, fillColor: '#F99963', fillOpacity: 0.12 }).addTo(state.map);
    const mLon = mPerDegLon(cLat), halfLon = (side / 2) / mLon, halfLat = (side / 2) / METRES_PER_DEG_LAT;
    state.squareBbox = [cLon - halfLon, cLat - halfLat, cLon + halfLon, cLat + halfLat];
    if (els.areaEcho) els.areaEcho.textContent = (side / 1000).toFixed(1) + ' × ' + (side / 1000).toFixed(1) + ' km';
    // a tipp kiemelve marad, amíg a kijelölés 15 km alatt van (ott elérhető a folyó/út réteg)
    const hint = root.querySelector('[data-tg-lines-hint]');
    if (hint) hint.classList.toggle('is-on', side <= 15000);
  }

  function showMapStep() {
    goStep('map');
    // Ha nincs térkép-könyvtár, essünk vissza a régi működésre (auto négyzet).
    if (!window.L || !els.mapEl) {
      if (els.mapEl) els.mapEl.innerHTML = '<div class="tg__mapfallback">Térkép nem elérhető — a teljes befoglaló terület lesz felhasználva.</div>';
      state.squareBbox = null;
      els.generate.disabled = false;
      setStatus('Kattints a jóváhagyásra a generáláshoz.');
      return;
    }
    initMap();
    if (state.mapLine) state.mapLine.remove();
    state.mapLine = window.L.polyline(state.track.map((p) => [p.lat, p.lon]),
      { color: '#F99963', weight: 3 }).addTo(state.map);
    const d = trackGroundDims();
    state._minSide = Math.max(d.gw, d.gh) * 1.05; // kis ráhagyás, hogy az útvonal beleférjen a lekerekítésbe
    state._center = [d.cLat, d.cLon];
    if (els.area) { els.area.min = 0; els.area.max = 100; els.area.step = 1; els.area.value = 15; }
    updateOutline();
    setTimeout(() => {
      state.map.invalidateSize();
      try { state.map.fitBounds(state.mapOutline.getBounds().pad(0.15)); } catch (e) {}
    }, 60);
    els.generate.disabled = false;
    setStatus('Állítsd be a lefedett területet, majd hagyd jóvá a generálást.');
  }

  if (els.area) els.area.addEventListener('input', updateOutline);

  /* ---------------------------------------------------------------- generate */
  // A Shopify CDN más origin, ezért a Worker közvetlen URL-ből nem tölthető.
  // A worker forrását letöltjük és azonos-origin blob URL-ből futtatjuk.
  let _workerBlobUrl = null;
  async function makeWorker() {
    if (!_workerBlobUrl) {
      const res = await fetch(cfg.workerUrl);
      if (!res.ok) throw new Error('worker HTTP ' + res.status);
      const src = await res.text();
      _workerBlobUrl = URL.createObjectURL(new Blob([src], { type: 'text/javascript' }));
    }
    return new Worker(_workerBlobUrl);
  }

  els.generate.addEventListener('click', async () => {
    if (state.busy || !state.bbox) return;
    state.busy = true;
    els.generate.disabled = true; els.addToCart.disabled = true;
    state.genStartedAt = Date.now();
    showProgress(true, 0, 'Indítás…');
    showOverlay(true, { title: 'Modell készítése…', sub: 'Indítás…', progress: true });

    try {
      if (state.worker) state.worker.terminate();
      state.worker = await makeWorker();
    } catch (err) {
      state.busy = false; showProgress(false); showOverlay(false); els.generate.disabled = false;
      setStatus('A generátor nem indult el: ' + err.message, true);
      return;
    }
    state.worker.onmessage = (ev) => {
      const m = ev.data;
      if (m.type === 'progress') { setProgress(m.pct, m.msg); setOverlayProgress(m.pct, m.msg); }
      else if (m.type === 'error') { state.busy = false; showProgress(false); showOverlay(false); els.generate.disabled = false; track_event('tervezo_generalas_hiba', { hiba: String(m.msg || '').slice(0, 100) }); setStatus(m.msg, true); }
      else if (m.type === 'result') onResult(m);
    };
    state.worker.onerror = (e) => { state.busy = false; showProgress(false); showOverlay(false); els.generate.disabled = false; setStatus('Feldolgozó hiba: ' + e.message, true); };
    state.worker.postMessage({ type: 'heightfield', params: { bbox: state.squareBbox || state.bbox, track: state.track, detail: FIXED.detail, square: state.squareBbox ? false : (cfg.squareFootprint !== false) } });
  });

  async function onResult(m) {
    state.busy = false; showProgress(false); showOverlay(false); els.generate.disabled = false;
    state.height = m.height; state.routeUV = m.routeUV; state.lastStats = m.stats; state.bbox = m.bbox;
    state.water = m.water || [];
    state.lines = m.lines || { rivers: [], roads: [] };
    state.linesAvailable = !!m.linesAvailable;
    state.sideKm = m.sideKm || 0;
    // A worker és a főszál összetartozásának ellenőrzése: eltérő verziónál a vízrajz
    // formátuma nem illeszkedik, és a víz csendben kimaradna a modellből.
    if (m.dataVersion !== DATA_VERSION && typeof console !== 'undefined') {
      console.warn('[tervező] FIGYELEM: a terrain-worker.js és a terrain-generator.js verziója nem egyezik'
        + ' (worker: ' + (m.dataVersion || 'régi') + ', oldal: ' + DATA_VERSION + ').'
        + ' Emiatt a vízrajz kimaradhat — töltsd fel MINDKÉT fájlt az Assets-be, majd hard-refresh.');
    }
    if (typeof console !== 'undefined') {
      const seas = state.water.filter((x) => x.klass === 'sea').length;
      console.info('[tervező] víz (OSM):', state.water.length - seas, 'tó/tározó' + (seas ? ' + tenger' : ' (tenger nincs)'),
        m.waterError ? ('| hiba: ' + m.waterError) : '', '| Overpass:', (m.waterLog || []).join(' → '));
    }
    updateExtrasUI();

    buildScene();               // create/refresh meshes at current exaggeration + colors

    // Sikeres generálás — mérés (a modell ekkor már látszik)
    track_event('tervezo_generalas', {
      terulet_km: +(m.height.groundW / 1000).toFixed(1),
      zoom: (m.stats && m.stats.zoom) || null,
      racs: (m.stats && m.stats.grid) || null,
      relief_m: (m.stats && m.stats.reliefMetres) || null,
      van_viz: (m.water || []).length > 0,
      folyo_ut_elerheto: !!m.linesAvailable,
      ido_ms: state.genStartedAt ? (Date.now() - state.genStartedAt) : null,
    });

    if (els.stats) els.stats.textContent = '';   // technikai infó — a vásárlónak nem mutatjuk
    setStatus('A modell kész. Állítsd a kiemelést és a színeket, majd tovább a kivitelhez.');
    goStep('result');
    if (state.three && state.three.onResize) setTimeout(state.three.onResize, 60);
  }

  /* --------------------------------------------- geometry (built main-thread) */
  // Footprint mask: returns true if normalized (u,v) is inside the fixed shape.
  function insideFn() {
    const r = 0.18;
    if (FIXED.footprint === 'rectangle' || FIXED.footprint === 'square') return () => true;
    return (u, v) => { // rounded_square / rounded
      const x = Math.min(u, 1 - u), y = Math.min(v, 1 - v);
      if (x > r || y > r) return true;
      const dx = r - x, dy = r - y; return dx * dx + dy * dy <= r * r;
    };
  }

  function geomMetrics() {
    const h = state.height;
    const mm = FIXED.sizeMM / Math.max(h.groundW, h.groundH);   // mm per ground-metre
    return { mm, halfX: h.groundW * mm / 2, halfY: h.groundH * mm / 2, spanX: h.groundW * mm, spanY: h.groundH * mm };
  }
  const nodeX = (i, gw, M) => -M.halfX + (i / (gw - 1)) * M.spanX;
  const nodeY = (j, gh, M) =>  M.halfY - (j / (gh - 1)) * M.spanY;
  function topZ(i, j) {
    const h = state.height;
    const relief = h.elev[j * h.gw + i] - h.zmin;
    return FIXED.baseMM + relief * geomMetrics().mm * currentScale();
  }
  function currentScale() { return parseFloat(els.vscale.value); }

  // Indexed terrain solid: top surface + flat bottom + boundary skirt.
  function buildTerrainArrays() {
    const h = state.height, M = geomMetrics(), inside = insideFn();
    const nodeIn = new Uint8Array(h.gw * h.gh);
    for (let j = 0; j < h.gh; j++) for (let i = 0; i < h.gw; i++)
      nodeIn[j * h.gw + i] = inside(i / (h.gw - 1), j / (h.gh - 1)) ? 1 : 0;
    const cellIn = (i, j) =>
      i >= 0 && j >= 0 && i < h.gw - 1 && j < h.gh - 1 &&
      nodeIn[j * h.gw + i] && nodeIn[j * h.gw + i + 1] &&
      nodeIn[(j + 1) * h.gw + i] && nodeIn[(j + 1) * h.gw + i + 1];

    const pos = [], idx = [], pool = new Map();

    // Smooth rounded corners without leaving notches. The footprint is cut per
    // grid-cell (stair-stepped). We relax ONLY the corner boundary vertices with
    // a few Laplacian passes, keeping the straight-edge boundary vertices fixed
    // as anchors — so the arcs smooth out and blend seamlessly into the edges.
    const GW = h.gw, GH = h.gh;
    const rounded = (FIXED.footprint !== 'rectangle' && FIXED.footprint !== 'square');
    const CR = 0.18; // must match the radius in insideFn()
    const isBoundaryNode = (i, j) => {
      if (!nodeIn[j * GW + i]) return false;
      for (let dj = -1; dj <= 1; dj++) for (let di = -1; di <= 1; di++) {
        const ni = i + di, nj = j + dj;
        if (ni < 0 || nj < 0 || ni >= GW || nj >= GH) return true;
        if (!nodeIn[nj * GW + ni]) return true;
      }
      return false;
    };
    const smU = new Float32Array(GW * GH), smV = new Float32Array(GW * GH);
    for (let j = 0; j < GH; j++) for (let i = 0; i < GW; i++) { smU[j * GW + i] = i / (GW - 1); smV[j * GW + i] = j / (GH - 1); }
    if (rounded) {
      const inCorner = (i, j) => {
        const u = i / (GW - 1), v = j / (GH - 1);
        return isBoundaryNode(i, j) && (u < CR || u > 1 - CR) && (v < CR || v > 1 - CR);
      };
      for (let it = 0; it < 4; it++) {
        const nu = smU.slice(), nv = smV.slice();
        for (let j = 0; j < GH; j++) for (let i = 0; i < GW; i++) {
          if (!inCorner(i, j)) continue;
          let su = 0, sv = 0, n = 0;
          for (let dj = -1; dj <= 1; dj++) for (let di = -1; di <= 1; di++) {
            const ni = i + di, nj = j + dj;
            if (ni < 0 || nj < 0 || ni >= GW || nj >= GH) continue;
            if (!isBoundaryNode(ni, nj)) continue;
            su += smU[nj * GW + ni]; sv += smV[nj * GW + ni]; n++;
          }
          if (n > 0) { nu[j * GW + i] = 0.5 * smU[j * GW + i] + 0.5 * (su / n); nv[j * GW + i] = 0.5 * smV[j * GW + i] + 0.5 * (sv / n); }
        }
        smU.set(nu); smV.set(nv);
      }
      // Re-project each corner vertex to the EXACT arc radius around its corner
      // centre. Laplacian evens out the angular spacing (kills the staircase);
      // this radial step restores the true radius so corners neither shrink nor
      // dent inward, and the arc stays tangent to the straight edges (no notch).
      for (let j = 0; j < GH; j++) for (let i = 0; i < GW; i++) {
        if (!inCorner(i, j)) continue;
        const u0 = i / (GW - 1), v0 = j / (GH - 1);
        const cx = u0 < 0.5 ? CR : 1 - CR, cy = v0 < 0.5 ? CR : 1 - CR;
        let du = smU[j * GW + i] - cx, dv = smV[j * GW + i] - cy;
        const d = Math.hypot(du, dv) || 1;
        smU[j * GW + i] = cx + du / d * CR;
        smV[j * GW + i] = cy + dv / d * CR;
      }
    }
    const nodeXY = (i, j) => {
      const u = smU[j * GW + i], v = smV[j * GW + i];
      return [-M.halfX + u * M.spanX, M.halfY - v * M.spanY];
    };

    const node = (i, j, top) => {
      const key = (top ? 't' : 'b') + i + '_' + j;
      let k = pool.get(key);
      if (k === undefined) { const xy = nodeXY(i, j); k = pos.length / 3; pos.push(xy[0], xy[1], top ? topZ(i, j) : 0); pool.set(key, k); }
      return k;
    };
    // Emit triangles with OUTWARD winding (verified via signed-volume test).
    const t = (a, b, c) => idx.push(a, c, b);
    let cells = 0;
    for (let j = 0; j < h.gh - 1; j++) for (let i = 0; i < h.gw - 1; i++) {
      if (!cellIn(i, j)) continue; cells++;
      const t00 = node(i, j, 1), t10 = node(i + 1, j, 1), t11 = node(i + 1, j + 1, 1), t01 = node(i, j + 1, 1);
      t(t00, t10, t11); t(t00, t11, t01);                       // top
      const b00 = node(i, j, 0), b10 = node(i + 1, j, 0), b11 = node(i + 1, j + 1, 0), b01 = node(i, j + 1, 0);
      t(b00, b11, b10); t(b00, b01, b11);                       // bottom (reversed)
      if (!cellIn(i, j - 1)) { t(t00, b00, b10); t(t00, b10, t10); }             // north
      if (!cellIn(i, j + 1)) { t(t11, b11, b01); t(t11, b01, t01); }             // south
      if (!cellIn(i - 1, j)) { t(t01, b01, b00); t(t01, b00, t00); }             // west
      if (!cellIn(i + 1, j)) { t(t10, b10, b11); t(t10, b11, t11); }             // east
    }
    if (!cells) throw new Error('empty footprint');
    return { positions: new Float32Array(pos), indices: new Uint32Array(idx) };
  }

  // Draped route ribbon (separate colored part, non-indexed).
  function buildRouteArrays() {
    const uv = state.routeUV; if (!uv || uv.length < 4) return null;
    const h = state.height, M = geomMetrics(), scale = currentScale();
    const hw = FIXED.routeWidthMM / 2, rise = FIXED.routeRiseMM;
    const reliefAt = (u, v) => {
      const gi = clamp(u * (h.gw - 1), 0, h.gw - 1), gj = clamp(v * (h.gh - 1), 0, h.gh - 1);
      const i0 = Math.floor(gi), j0 = Math.floor(gj), i1 = Math.min(i0 + 1, h.gw - 1), j1 = Math.min(j0 + 1, h.gh - 1);
      const fx = gi - i0, fy = gj - j0;
      const e = (a, b) => a * (1 - fx) + b * fx;
      const top = e(h.elev[j0 * h.gw + i0], h.elev[j0 * h.gw + i1]);
      const bot = e(h.elev[j1 * h.gw + i0], h.elev[j1 * h.gw + i1]);
      return (top * (1 - fy) + bot * fy) - h.zmin;
    };
    // Nyers középvonal a bbox-normalizált útvonalból, a terepre fektetve.
    const raw = [];
    for (let k = 0; k < uv.length; k += 2) {
      const u = uv[k], v = uv[k + 1];
      const X = -M.halfX + u * M.spanX, Y = M.halfY - v * M.spanY;
      const Z = FIXED.baseMM + reliefAt(u, v) * M.mm * scale + rise;
      raw.push([X, Y, Z]);
    }
    if (raw.length < 2) return null;

    // 1) ritkítás: a túl közeli (zajos) pontokat kihagyjuk
    const minGap = Math.max(hw * 0.8, 0.7);
    const dec = [raw[0]];
    for (let k = 1; k < raw.length; k++) {
      const p = raw[k], q = dec[dec.length - 1];
      if (Math.hypot(p[0] - q[0], p[1] - q[1]) >= minGap) dec.push(p);
    }
    const lastRaw = raw[raw.length - 1];
    if (dec[dec.length - 1] !== lastRaw) dec.push(lastRaw);

    // 2) simítás (mozgóátlag, pár menet) — főleg a Z-t, hogy ne "gyöngyözzön" a terepen
    let sm = dec.map((p) => p.slice());
    for (let it = 0; it < 2 && sm.length > 2; it++) {
      const nb = sm.map((p) => p.slice());
      for (let k = 1; k < sm.length - 1; k++)
        for (let d = 0; d < 3; d++) nb[k][d] = (sm[k - 1][d] + sm[k][d] + sm[k + 1][d]) / 3;
      sm = nb;
    }

    // 3) sima, egyenletes középvonal Catmull-Rom görbéből (centripetális → nincs túllövés)
    let pts;
    if (sm.length >= 3) {
      const curve = new THREE.CatmullRomCurve3(sm.map((p) => new THREE.Vector3(p[0], p[1], p[2])), false, 'centripetal', 0.5);
      const len = curve.getLength();
      const N = Math.min(4000, Math.max(2, Math.round(len / Math.max(0.6, hw * 0.5))));
      pts = curve.getSpacedPoints(N).map((v) => [v.x, v.y, v.z]);
    } else {
      pts = sm;
    }
    if (pts.length < 2) return null;
    const L = [], R = [];
    for (let k = 0; k < pts.length; k++) {
      const a = pts[Math.max(0, k - 1)], b = pts[Math.min(pts.length - 1, k + 1)];
      let dx = b[0] - a[0], dy = b[1] - a[1]; const len = Math.hypot(dx, dy) || 1; dx /= len; dy /= len;
      const nx = -dy, ny = dx;
      L.push([pts[k][0] + nx * hw, pts[k][1] + ny * hw, pts[k][2]]);
      R.push([pts[k][0] - nx * hw, pts[k][1] - ny * hw, pts[k][2]]);
    }
    const pos = [];
    const tri = (A, B, C) => pos.push(A[0], A[1], A[2], B[0], B[1], B[2], C[0], C[1], C[2]);
    const drop = (P) => [P[0], P[1], P[2] - rise];
    for (let k = 0; k < pts.length - 1; k++) {
      const l0 = L[k], l1 = L[k + 1], r0 = R[k], r1 = R[k + 1];
      tri(l0, r0, r1); tri(l0, r1, l1);                     // top
      tri(l0, l1, drop(l1)); tri(l0, drop(l1), drop(l0));   // left wall
      tri(r1, r0, drop(r0)); tri(r1, drop(r0), drop(r1));   // right wall
      tri(drop(l0), drop(r1), drop(r0)); tri(drop(l0), drop(l1), drop(r1)); // bottom
    }
    const cap = (a, b) => { tri(a, b, drop(b)); tri(a, drop(b), drop(a)); };
    cap(R[0], L[0]); cap(L[pts.length - 1], R[pts.length - 1]);
    const positions = new Float32Array(pos);
    const indices = new Uint32Array(pos.length / 3); for (let i = 0; i < indices.length; i++) indices[i] = i;
    return { positions, indices };
  }

  /* ======================= VÍZ (OSM poligonokból) =======================
   * A víz külön, sík lap: a vízszint fölé egy hajszállal emelve,
   * WATER_DEPTH_MM-rel a terepbe süllyesztve, hogy összeforrjanak.
   * A terepet SOSEM vágjuk/simítjuk a víz alatt: ahol a föld a vízszint fölé
   * emelkedik (szigetek, part), ott egyszerűen kilátszik a vízből.
   * ==================================================================== */
  const WATER_COLOR = 0x0078BF;
  const WATER_DEPTH_MM = 2.5;      // ennyivel süllyed a terepbe
  // A vízlap emelése a vízszint fölé. FONTOS: fix mm-ben megadva ez valós méretben
  // sok métert jelenthet (nagy területnél 0,5 mm ~ 40 m), ami elárasztaná az alacsony
  // partot és a kis szigeteket. Ezért a cél ~1,5 valós méter, mm-ben korlátozva:
  // legalább 0,05 mm (hogy a mélységpuffer biztosan szétválassza), legfeljebb 0,25 mm.
  const WATER_RISE_TARGET_M = 1.5, WATER_RISE_MIN_MM = 0.05, WATER_RISE_MAX_MM = 0.15;
  function waterRiseMM() {
    const mmPerMetre = geomMetrics().mm * currentScale();
    return clamp(WATER_RISE_TARGET_M * mmPerMetre, WATER_RISE_MIN_MM, WATER_RISE_MAX_MM);
  }
  const WATER_LEVEL_PCT = 20;      // a partmagasságok 20. percentilise = vízszint
  const DATA_VERSION = 'tg-vec-1';           // meg kell egyeznie a terrain-worker.js verziójával
  const MIN_WATER_AREA_M2 = 10000000;  // 10 km² — ennél kisebb tavak nem jelennek meg (a tenger kivétel)
  const WATER_ROUTE_TRIM_MM = 0.02; // a víz gyakorlatilag az útvonal széléig ér (kis átfedés megengedett)
  const WATER_MASK_TARGET = 1200;  // a vízmaszk cél-felbontása (a terep rácsának 1-4x-e)

  function reliefAtUV(u, v) {
    const h = state.height;
    const gi = clamp(u * (h.gw - 1), 0, h.gw - 1), gj = clamp(v * (h.gh - 1), 0, h.gh - 1);
    const i0 = Math.floor(gi), j0 = Math.floor(gj), i1 = Math.min(i0 + 1, h.gw - 1), j1 = Math.min(j0 + 1, h.gh - 1);
    const fx = gi - i0, fy = gj - j0, e = (a, b) => a * (1 - fx) + b * fx;
    const top = e(h.elev[j0 * h.gw + i0], h.elev[j0 * h.gw + i1]);
    const bot = e(h.elev[j1 * h.gw + i0], h.elev[j1 * h.gw + i1]);
    return (top * (1 - fy) + bot * fy) - h.zmin;
  }
  function lonLatToMM(lon, lat) {
    const [w, s, e, n] = state.bbox, M = geomMetrics();
    return [-M.halfX + ((lon - w) / (e - w)) * M.spanX, M.halfY - ((n - lat) / (n - s)) * M.spanY];
  }
  function terrainZmm(lon, lat) {
    const [w, s, e, n] = state.bbox, M = geomMetrics();
    const u = clamp((lon - w) / (e - w), 0, 1), v = clamp((n - lat) / (n - s), 0, 1);
    return FIXED.baseMM + reliefAtUV(u, v) * M.mm * currentScale();
  }
  function footprintPolygonMM() {
    const M = geomMetrics(), hx = M.halfX, hy = M.halfY, fp = FIXED.footprint;
    if (fp !== 'rounded_square' && fp !== 'rounded') return [[-hx, -hy], [hx, -hy], [hx, hy], [-hx, hy]];
    const r = 0.18 * Math.min(M.spanX, M.spanY), SEG = 12, out = [];
    const corners = [[hx - r, hy - r, 0], [-hx + r, hy - r, Math.PI / 2], [-hx + r, -hy + r, Math.PI], [hx - r, -hy + r, 1.5 * Math.PI]];
    for (const c of corners)
      for (let k = 0; k <= SEG; k++) { const a = c[2] + (Math.PI / 2) * k / SEG; out.push([c[0] + r * Math.cos(a), c[1] + r * Math.sin(a)]); }
    return out;
  }
  const polyArea2 = (p) => { let a = 0; for (let i = 0, n = p.length; i < n; i++) { const q = p[(i + 1) % n]; a += p[i][0] * q[1] - q[0] * p[i][1]; } return a / 2; };
  function clipToConvex(subject, clip) {
    let out = subject;
    for (let i = 0, n = clip.length; i < n && out.length; i++) {
      const A = clip[i], B = clip[(i + 1) % n];
      const side = (p) => (B[0] - A[0]) * (p[1] - A[1]) - (B[1] - A[1]) * (p[0] - A[0]);
      const inp = out; out = [];
      for (let k = 0; k < inp.length; k++) {
        const P = inp[k], Q = inp[(k + 1) % inp.length], sp = side(P), sq = side(Q);
        if (sp >= 0) out.push(P);
        if ((sp > 0 && sq < 0) || (sp < 0 && sq > 0)) {
          const t = sp / (sp - sq);
          out.push([P[0] + t * (Q[0] - P[0]), P[1] + t * (Q[1] - P[1])]);
        }
      }
    }
    return out;
  }
  const percentileOf = (arr, pct) => {
    if (!arr.length) return 0;
    const a = arr.slice().sort((x, y) => x - y);
    return a[clamp(Math.round((pct / 100) * (a.length - 1)), 0, a.length - 1)];
  };

  // Poligon kitöltése CELLA-KÖZÉPPONTOK alapján (scanline, páros-páratlan szabály).
  // TÖBB gyűrűt egyszerre kezel, így a belső gyűrűk (lyukak — pl. sziget a tóban)
  // automatikusan kimaradnak. Középpont-alapú, ezért a víz széle nem húzódik hátra.
  function fillPolygonCells(rings, mw, mh, plot) {
    let minY = Infinity, maxY = -Infinity;
    for (const ring of rings) for (const p of ring) { if (p[1] < minY) minY = p[1]; if (p[1] > maxY) maxY = p[1]; }
    if (!isFinite(minY)) return;
    const j0 = Math.max(0, Math.floor(minY - 0.5)), j1 = Math.min(mh - 1, Math.ceil(maxY));
    const xs = [];
    for (let j = j0; j <= j1; j++) {
      const y = j + 0.5;
      xs.length = 0;
      for (const ring of rings) {
        for (let a = 0, n = ring.length; a < n; a++) {
          const p = ring[a], q = ring[(a + 1) % n];
          if ((p[1] <= y && q[1] > y) || (q[1] <= y && p[1] > y))
            xs.push(p[0] + (y - p[1]) / (q[1] - p[1]) * (q[0] - p[0]));
        }
      }
      xs.sort((a, b) => a - b);
      for (let m = 0; m + 1 < xs.length; m += 2) {
        const ia = Math.max(0, Math.ceil(xs[m] - 0.5)), ib = Math.min(mw - 1, Math.floor(xs[m + 1] - 0.5));
        for (let i = ia; i <= ib; i++) plot(i, j);
      }
    }
  }

  function buildWaterArrays() {
    if (!state.water || !state.water.length || !state.bbox || !state.height) return null;
    const h = state.height, M = geomMetrics(), inside = insideFn();
    const [W, S, E, N] = state.bbox;
    // Finomabb rács a víz peremének: a lépcsőzés a terep rácsának 1/SUB-ára csökken.
    const SUB = Math.max(1, Math.min(4, Math.floor(WATER_MASK_TARGET / h.gw)));
    const mw = (h.gw - 1) * SUB, mh = (h.gh - 1) * SUB;         // CELLÁK száma
    const mask = new Uint8Array(mw * mh), level = new Float32Array(mw * mh);
    const cellMM = M.spanX / mw;
    const RISE = waterRiseMM();
    // cella-index -> modell mm (a cella sarka)
    const cx = (i) => -M.halfX + (i / mw) * M.spanX;
    const cy = (j) => M.halfY - (j / mh) * M.spanY;

    // 1) a vízpoligonok rárajzolása a rácsra, a saját vízszintjükkel
    const seaMask = new Uint8Array(mw * mh);
    for (const entry of state.water) {
      const rings = entry.rings || (entry.coords ? [entry.coords] : []);
      if (!rings.length || rings[0].length < 3) continue;
      const zWater = percentileOf(rings[0].map((p) => terrainZmm(p[0], p[1])), WATER_LEVEL_PCT);
      const gr = rings.map((r) => r.map((p) => [
        (p[0] - W) / (E - W) * mw,
        (N - p[1]) / (N - S) * mh,
      ]));
      const isSea = entry.klass === 'sea';
      fillPolygonCells(gr, mw, mh, (i, j) => {
        const k = j * mw + i;
        if (!mask[k] || level[k] < zWater) { mask[k] = 1; level[k] = zWater; }
        if (isSea) seaMask[k] = 1;
      });
    }

    // 1b) MÉRETSZŰRÉS a maszkon: a 20 km²-nél kisebb összefüggő vízfoltok kimaradnak.
    //     A csempehatárok mentén darabolt poligonok itt már összeértek, ezért poligon
    //     helyett a maszkon szűrünk. A tenger sosem esik ki.
    {
      const cellM2 = (h.groundW / mw) * (h.groundH / mh);
      const minCells = Math.max(1, Math.round(MIN_WATER_AREA_M2 / cellM2));
      const seen = new Uint8Array(mw * mh), stack = new Int32Array(mw * mh), comp = new Int32Array(mw * mh);
      for (let start = 0; start < mask.length; start++) {
        if (!mask[start] || seen[start]) continue;
        let sp = 0, n = 0, sea = false;
        stack[sp++] = start; seen[start] = 1;
        while (sp > 0) {
          const idx = stack[--sp];
          comp[n++] = idx;
          if (seaMask[idx]) sea = true;
          const x = idx % mw, y = (idx - x) / mw;
          if (x > 0 && mask[idx - 1] && !seen[idx - 1]) { seen[idx - 1] = 1; stack[sp++] = idx - 1; }
          if (x < mw - 1 && mask[idx + 1] && !seen[idx + 1]) { seen[idx + 1] = 1; stack[sp++] = idx + 1; }
          if (y > 0 && mask[idx - mw] && !seen[idx - mw]) { seen[idx - mw] = 1; stack[sp++] = idx - mw; }
          if (y < mh - 1 && mask[idx + mw] && !seen[idx + mw]) { seen[idx + mw] = 1; stack[sp++] = idx + mw; }
        }
        if (!sea && n < minCells) for (let k = 0; k < n; k++) mask[comp[k]] = 0;
      }
    }

    // 2) az útvonal korridorja: a víz épp az útvonal széléig ér (kis átfedéssel, hézag nélkül)
    if (state.routeUV && state.routeUV.length >= 4) {
      const rMM = Math.max(0.1, (FIXED.routeWidthMM / 2) - WATER_ROUTE_TRIM_MM);
      const rC = rMM / cellMM, rr = Math.ceil(rC), r2 = rC * rC;
      for (let t = 0; t + 3 < state.routeUV.length; t += 2) {
        const ax = state.routeUV[t] * mw - 0.5, ay = state.routeUV[t + 1] * mh - 0.5;
        const bx = state.routeUV[t + 2] * mw - 0.5, by = state.routeUV[t + 3] * mh - 0.5;
        const i0 = Math.max(0, Math.floor(Math.min(ax, bx)) - rr), i1 = Math.min(mw - 1, Math.ceil(Math.max(ax, bx)) + rr);
        const j0 = Math.max(0, Math.floor(Math.min(ay, by)) - rr), j1 = Math.min(mh - 1, Math.ceil(Math.max(ay, by)) + rr);
        const dx = bx - ax, dy = by - ay, len2 = (dx * dx + dy * dy) || 1;
        for (let j = j0; j <= j1; j++) for (let i = i0; i <= i1; i++) {
          const k = j * mw + i;
          if (!mask[k]) continue;
          let tt = ((i - ax) * dx + (j - ay) * dy) / len2;
          tt = tt < 0 ? 0 : (tt > 1 ? 1 : tt);
          const px = ax + tt * dx - i, py = ay + tt * dy - j;
          if (px * px + py * py <= r2) mask[k] = 0;
        }
      }
    }

    // 3) a talp: a víz PONTOSAN a terep talpmaszkjához kötve. A terep cellái a durva
    //    rácson épülnek (minden sarka a kontúron belül), a víz a finomabb rácson —
    //    ezért a víz különben hajszállal túlnyúlna a modell peremén (kék vonal).
    const nodeIn = new Uint8Array(h.gw * h.gh);
    for (let j = 0; j < h.gh; j++) for (let i = 0; i < h.gw; i++)
      nodeIn[j * h.gw + i] = inside(i / (h.gw - 1), j / (h.gh - 1)) ? 1 : 0;
    const coarseSolid = (ci, cj) => nodeIn[cj * h.gw + ci] && nodeIn[cj * h.gw + ci + 1]
      && nodeIn[(cj + 1) * h.gw + ci] && nodeIn[(cj + 1) * h.gw + ci + 1];
    const outside = new Uint8Array(mw * mh);
    for (let j = 0; j < mh; j++) for (let i = 0; i < mw; i++) {
      const k = j * mw + i;
      if (!coarseSolid(Math.floor(i / SUB), Math.floor(j / SUB))) { outside[k] = 1; mask[k] = 0; }
    }

    // 4) geometria: a felső lapot SORONKÉNT összevonjuk (kevés háromszög),
    //    a peremen pedig oldalfal megy le a terepbe süllyesztett szintre.
    const pos = [], idx = [];
    const quad = (x0, x1, y0, y1, z) => {                       // vízszintes lap
      const b = pos.length / 3;
      pos.push(x0, y0, z, x1, y0, z, x1, y1, z, x0, y1, z);
      idx.push(b, b + 2, b + 1, b, b + 3, b + 2);
    };
    const wall = (x0, y0, x1, y1, zTop, zBot) => {              // oldalfal
      const b = pos.length / 3;
      pos.push(x0, y0, zTop, x1, y1, zTop, x1, y1, zBot, x0, y0, zBot);
      idx.push(b, b + 1, b + 2, b, b + 2, b + 3);
    };
    let cells = 0;
    for (let j = 0; j < mh; j++) {
      let i = 0;
      while (i < mw) {
        const k = j * mw + i;
        if (!mask[k]) { i++; continue; }
        const lv = level[k];
        let i2 = i;
        while (i2 + 1 < mw && mask[j * mw + i2 + 1] && level[j * mw + i2 + 1] === lv) i2++;
        cells += i2 - i + 1;
        quad(cx(i), cx(i2 + 1), cy(j), cy(j + 1), lv + RISE);
        i = i2 + 1;
      }
    }
    // perem: oldalfalak (csak ahol nincs vizes szomszéd)
    for (let j = 0; j < mh; j++) for (let i = 0; i < mw; i++) {
      const k = j * mw + i;
      if (!mask[k]) continue;
      const zT = level[k] + RISE, zB = Math.max(level[k] - WATER_DEPTH_MM, 0);
      const x0 = cx(i), x1 = cx(i + 1), y0 = cy(j), y1 = cy(j + 1);
      // oldalfal csak BELSŐ peremen (part, sziget, útvonal-korridor) — a modell szélén nem
      if (j > 0 && !mask[k - mw] && !outside[k - mw]) wall(x0, y0, x1, y0, zT, zB);
      if (j < mh - 1 && !mask[k + mw] && !outside[k + mw]) wall(x1, y1, x0, y1, zT, zB);
      if (i > 0 && !mask[k - 1] && !outside[k - 1]) wall(x0, y1, x0, y0, zT, zB);
      if (i < mw - 1 && !mask[k + 1] && !outside[k + 1]) wall(x1, y0, x1, y1, zT, zB);
    }
    if (!cells) return null;
    return { positions: new Float32Array(pos), indices: new Uint32Array(idx), maskCells: cells, sub: SUB };
  }

  /* ============ FOLYÓK ÉS UTAK (OSM vonalak, csak kis területnél) ============ */
  const RIVER_COLOR = 0x0078BF;                 // folyók (a vízzel azonos kék)
  const RIVER_WIDTH_MM = 1.2, RIVER_RISE_MM = 0.25;
  const ROAD_WIDTH_MM = 1.0, ROAD_RISE_MM = 0.2;

  function emitRibbon(pts, halfW, pos, idx) {
    const base = pos.length / 3, n = pts.length;
    for (let i = 0; i < n; i++) {
      const a = pts[Math.max(0, i - 1)], b = pts[Math.min(n - 1, i + 1)];
      let tx = b[0] - a[0], ty = b[1] - a[1];
      const len = Math.hypot(tx, ty) || 1; tx /= len; ty /= len;
      const nx = -ty * halfW, ny = tx * halfW, p = pts[i];
      pos.push(p[0] + nx, p[1] + ny, p[2], p[0] - nx, p[1] - ny, p[2]);
    }
    for (let i = 0; i + 1 < n; i++) {
      const a = base + i * 2, b = a + 1, c = a + 2, d = a + 3;
      idx.push(a, c, b, b, c, d);
    }
  }

  // lon/lat vonalláncokból szalagok a terep felszínére fektetve
  function buildLineArrays(lines, widthMM, riseMM) {
    if (!lines || !lines.length || !state.height || !state.bbox) return null;
    const M = geomMetrics(), scale = currentScale(), inside = insideFn();
    const [W, S, E, N] = state.bbox, halfW = widthMM / 2;
    const pos = [], idx = [];
    for (const line of lines) {
      let seg = [];
      const flush = () => { if (seg.length >= 2) emitRibbon(seg, halfW, pos, idx); seg = []; };
      for (const p of line) {
        const u = (p[0] - W) / (E - W), v = (N - p[1]) / (N - S);
        if (u < 0 || u > 1 || v < 0 || v > 1 || !inside(u, v)) { flush(); continue; }
        seg.push([-M.halfX + u * M.spanX, M.halfY - v * M.spanY,
          FIXED.baseMM + reliefAtUV(u, v) * M.mm * scale + riseMM]);
      }
      flush();
    }
    if (!idx.length) return null;
    return { positions: new Float32Array(pos), indices: new Uint32Array(idx) };
  }

  function linesData(kind) { return (state.lines && state.lines[kind]) || []; }
  function toggleOn(kind) { const t = root.querySelector('[data-tg-toggle="' + kind + '"]'); return !!(t && t.checked); }

  // a folyó/út réteg felépítése vagy frissítése (a kiemelés-csúszkára is)
  function refreshLineLayer(kind) {
    if (!state.group) return;
    const meshKey = kind === 'rivers' ? 'meshRivers' : 'meshRoads';
    const on = toggleOn(kind);
    const data = on ? buildLineArrays(linesData(kind),
      kind === 'rivers' ? RIVER_WIDTH_MM : ROAD_WIDTH_MM,
      kind === 'rivers' ? RIVER_RISE_MM : ROAD_RISE_MM) : null;
    if (state[meshKey]) {
      state.group.remove(state[meshKey]);
      state[meshKey].geometry.dispose();
      state[meshKey] = null;
    }
    if (!data) return;
    const color = kind === 'rivers' ? RIVER_COLOR : (els.colorRoad ? els.colorRoad.value : '#000000');
    state[meshKey] = new THREE.Mesh(geomToBuffer(data),
      new THREE.MeshStandardMaterial({ color, roughness: 0.8, metalness: 0, side: THREE.DoubleSide }));
    state[meshKey].name = kind;
    state.group.add(state[meshKey]);
  }
  function refreshLineLayers() { refreshLineLayer('rivers'); refreshLineLayer('roads'); }

  // A vízréteg (tenger/tavak) felépítése vagy újraépítése a már meglévő modellre.
  function refreshWaterLayer() {
    if (!state.group) return;
    if (state.meshWater) {
      state.group.remove(state.meshWater);
      state.meshWater.geometry.dispose();
      if (state.meshWater.material && state.meshWater.material.dispose) state.meshWater.material.dispose();
      state.meshWater = null;
    }
    const wa = buildWaterArrays();
    if (!wa) return;
    state.meshWater = new THREE.Mesh(geomToBuffer(wa), new THREE.MeshStandardMaterial({
      color: WATER_COLOR, roughness: 0.35, metalness: 0.05, side: THREE.DoubleSide,
    }));
    state.meshWater.name = 'water';
    state.group.add(state.meshWater);
  }

  // A folyó/út kapcsolók csak akkor jelennek meg, ha van mit mutatni (<= 15 km terület).
  // Egy színválasztó olvasható értéke a rendeléshez: „Sötétzöld (#68724D)".
  // A nevet a kiválasztott korong `data-name` attribútumából olvassuk, így ha
  // a palettát átnevezed vagy bővíted, ez magától követi.
  function colorLabel(input) {
    const hex = (input && input.value) || '';
    const wrap = input && input.closest ? input.closest('[data-tg-swatch]') : null;
    const sel = wrap ? wrap.querySelector('.tg__swatch-opt.is-on') : null;
    const name = sel ? (sel.getAttribute('data-name') || '') : '';
    return name ? (name + ' (' + hex + ')') : hex;
  }
  function roadColorLabel() { return colorLabel(els.colorRoad); }
  function updateExtrasUI() {
    if (!els.extras) return;
    const has = linesData('rivers').length + linesData('roads').length;
    els.extras.hidden = !(state.linesAvailable && has > 0);
    if (els.roadColorWrap) els.roadColorWrap.hidden = !toggleOn('roads');
  }
  root.querySelectorAll('[data-tg-toggle]').forEach((t) => {
    t.addEventListener('change', () => {
      const kind = t.getAttribute('data-tg-toggle');
      if (kind === 'roads' && els.roadColorWrap) els.roadColorWrap.hidden = !t.checked;
      refreshLineLayer(kind);
    });
  });
  if (els.colorRoad) els.colorRoad.addEventListener('input', () => {
    if (state.meshRoads) state.meshRoads.material.color.set(els.colorRoad.value);
  });

  // NFC link: opcionális. Hiányzó séma esetén https://-t teszünk elé, majd ellenőrizzük.
  function nfcValue() {
    if (!els.nfcUrl) return { url: '', ok: true, empty: true };
    let v = (els.nfcUrl.value || '').trim();
    if (!v) return { url: '', ok: true, empty: true };
    if (!/^https?:\/\//i.test(v)) v = 'https://' + v;
    try {
      const u = new URL(v);
      const ok = /^https?:$/.test(u.protocol) && u.hostname.indexOf('.') > 0;
      return { url: ok ? u.href : v, ok, empty: false };
    } catch (e) { return { url: v, ok: false, empty: false }; }
  }
  function showNfcMsg(text, kind) {
    if (!els.nfcMsg) return;
    els.nfcMsg.textContent = text || '';
    els.nfcMsg.hidden = !text;
    els.nfcMsg.classList.toggle('is-error', kind === 'error');
    els.nfcMsg.classList.toggle('is-ok', kind === 'ok');
  }
  if (els.nfcUrl) {
    els.nfcUrl.addEventListener('blur', () => {
      const n = nfcValue();
      if (n.empty) return showNfcMsg('', null);
      if (!n.ok) return showNfcMsg('Ez nem tűnik érvényes linknek — ellenőrizd, kérlek.', 'error');
      els.nfcUrl.value = n.url;                    // kiegészített, tisztított cím
      showNfcMsg('Ezt írjuk majd a chipre.', 'ok');
    });
    els.nfcUrl.addEventListener('input', () => showNfcMsg('', null));
  }

  /* ============================ MÉRÉS / ANALITIKA ============================
   * Eseményeket küldünk a boltban EGYÁLTALÁN elérhető célokba. Mindegyik opcionális:
   * ha nincs telepítve, csendben kimarad (nincs hiba, nincs késleltetés).
   *   - Google Tag Manager  -> window.dataLayer
   *   - Google Analytics 4  -> gtag('event', ...)
   *   - Shopify Web Pixels  -> Shopify.analytics.publish(...)
   *   - Meta Pixel          -> fbq('trackCustom', ...)
   * ======================================================================== */
  // Ha a szekcióban meg van adva a GA4 azonosító és az oldalon még nincs gtag,
  // a modul MAGA betölti — így nem kell a témát szerkeszteni.
  (function initGa4() {
    const id = (cfg.ga4Id || '').trim();
    if (!id || typeof window === 'undefined') return;
    if (typeof window.gtag === 'function') return;                  // már van Google tag
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () { window.dataLayer.push(arguments); };
    const sc = document.createElement('script');
    sc.async = true;
    sc.src = 'https://www.googletagmanager.com/gtag/js?id=' + encodeURIComponent(id);
    document.head.appendChild(sc);
    window.gtag('js', new Date());
    window.gtag('config', id, { send_page_view: false });           // az oldalletöltést nem duplikáljuk
    if (cfg.debugTracking && typeof console !== 'undefined') console.info('[tervező] GA4 betöltve:', id);
  })();

  function track_event(name, params) {
    const p = params || {};
    const sent = [];
    try { if (window.dataLayer && window.dataLayer.push) { window.dataLayer.push(Object.assign({ event: name }, p)); sent.push('dataLayer'); } } catch (e) {}
    try { if (typeof window.gtag === 'function') { window.gtag('event', name, p); sent.push('gtag/GA4'); } } catch (e) {}
    try {
      const sa = window.Shopify && window.Shopify.analytics;
      if (sa && typeof sa.publish === 'function') { sa.publish(name, p); sent.push('Shopify pixel'); }
    } catch (e) {}
    try { if (typeof window.fbq === 'function') { window.fbq('trackCustom', name, p); sent.push('Meta'); } } catch (e) {}
    try {
      if (cfg.debugTracking && typeof console !== 'undefined') {
        if (sent.length) console.info('[tervező] esemény:', name, p, '→ elküldve:', sent.join(', '));
        else console.warn('[tervező] esemény:', name, '→ NEM MENT EL: nincs elérhető analitika (se GA4/gtag, se GTM, se Shopify pixel).');
      }
    } catch (e) {}
  }

  /* --------------------------------------------------------------- scene */
  function buildScene() {
    if (!state.three) state.three = createThree(els.viewer);
    const { scene } = state.three;
    if (state.group) { disposeGroup(state.group); scene.remove(state.group); }
    const group = new THREE.Group();
    state.group = group;                 // MÁR ITT be kell állítani: a víz/folyó/út rétegek ehhez adódnak

    const gt = geomToBuffer(buildTerrainArrays());
    state.meshTerrain = new THREE.Mesh(gt, new THREE.MeshStandardMaterial({ color: els.colorTerrain.value, roughness: 0.95, metalness: 0, flatShading: true }));
    group.add(state.meshTerrain);

    const ra = buildRouteArrays();
    if (ra) {
      const gr = geomToBuffer(ra);
      state.meshRoute = new THREE.Mesh(gr, new THREE.MeshStandardMaterial({ color: els.colorRoute.value, roughness: 0.6, metalness: 0 }));
      group.add(state.meshRoute);
    } else state.meshRoute = null;

    refreshWaterLayer();

    refreshLineLayers();

    group.rotation.x = -Math.PI / 2;   // view +Y up (geometry stays +Z up for export)
    scene.add(group);
    frameObject(state.three, group);
  }

  function geomToBuffer(a) {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(a.positions, 3));
    g.setIndex(new THREE.BufferAttribute(a.indices, 1));
    g.computeVertexNormals();
    g.computeBoundingSphere();
    return g;
  }

  /* ------------------------------------------ live exaggeration + colors */
  els.vscale.addEventListener('input', () => {
    els.vscaleEcho.textContent = els.vscale.value;
    if (!state.height || state.rafPending) return;
    state.rafPending = true;
    requestAnimationFrame(() => {
      state.rafPending = false;
      // rebuild geometry from cached heightfield (per-vertex Z recompute)
      state.meshTerrain.geometry.dispose(); state.meshTerrain.geometry = geomToBuffer(buildTerrainArrays());
      refreshLineLayers();
      refreshWaterLayer();
      const ra = buildRouteArrays();
      if (ra && state.meshRoute) { state.meshRoute.geometry.dispose(); state.meshRoute.geometry = geomToBuffer(ra); }
    });
  });
  els.colorTerrain.addEventListener('input', () => { if (state.meshTerrain) state.meshTerrain.material.color.set(els.colorTerrain.value); });
  els.colorRoute.addEventListener('input', () => { if (state.meshRoute) state.meshRoute.material.color.set(els.colorRoute.value); });

  /* ----------------------------------------------------------- add to cart */
  els.addToCart.addEventListener('click', async () => {
    if (!state.height) return;
    if (!state.selectedVariant) { setStatus('Válassz kivitelt és méretet.', true); return; }
    const nfc = nfcValue();
    if (!nfc.ok) {                                  // hibás linkkel ne induljon el a kosárba tétel
      showNfcMsg('Ez nem tűnik érvényes linknek — ellenőrizd, kérlek.', 'error');
      if (els.nfcUrl) els.nfcUrl.focus();
      return;
    }
    els.addToCart.disabled = true;
    showOverlay(true, { title: 'Mentés és kosárba helyezés…', sub: 'Egy pillanat, feltöltjük a terved.' });

    // FORRÁS-RECEPT: csak a szükséges paraméterek — a domborzatot NEM mentjük,
    // azt a rekonstrukció a bbox-ból újratölti (AWS Terrain Tiles).
    const frozen = {
      version: cfg.toolVersion,
      generatedAt: new Date().toISOString(),
      options: {
        verticalScale: currentScale(), colorTerrain: els.colorTerrain.value, colorRoute: els.colorRoute.value,
        rivers: toggleOn('rivers'), roads: toggleOn('roads'),
        colorRoad: (els.colorRoad && els.colorRoad.value) || null,
      },
      fixed: FIXED,
      bbox: state.bbox,                         // a kiválasztott (négyzetes) terület: hely + méret
      routeUV: Array.from(state.routeUV || []), // útvonal a bbox-ra normálva [u,v,...]
      gpx: state.gpxFile ? state.gpxFile.name : null,
    };
    const frozenFile = new File([JSON.stringify(frozen)], 'terrain-source.json', { type: 'application/json' });

    // A GPX lehet nagy → gzip-eljük (natív, könyvtár nélkül). A JSON már pici, marad sima.
    async function maybeGzip(blob) {
      if (typeof CompressionStream === 'undefined') return { blob: blob, gz: false };
      try {
        const gz = await new Response(blob.stream().pipeThrough(new CompressionStream('gzip'))).blob();
        return { blob: gz, gz: true };
      } catch (e) { return { blob: blob, gz: false }; }
    }

    // Manual FormData -> /cart/add.js so file line-item-properties actually attach
    // (the stock theme handler serializes FormData and drops files).
    const fd = new FormData();
    fd.append('id', state.selectedVariant);
    fd.append('quantity', '1');
    fd.append('properties[Kivitel]', state.selectedKivitel || '');
    if (!nfc.empty) fd.append('properties[NFC link]', nfc.url);
    fieldDefs.forEach((f) => {
      if (f.forVariants.indexOf(state.selectedType) === -1) return;
      const v = (f.input.value || '').trim();
      if (v) fd.append('properties[' + f.label + ']', v);
    });
    if (state.selectedType === 'stand') {
      const layout = plateLayoutText();
      if (layout) fd.append('properties[_Névtábla elrendezés]', layout);
    }
    fd.append('properties[_Domborzat kiemelése]', currentScale() + '×');
    fd.append('properties[_Terep színe]', colorLabel(els.colorTerrain));
    fd.append('properties[_Útvonal színe]', colorLabel(els.colorRoute));
    // A folyó/út választás MINDIG mentődik (ha egyáltalán felkínáltuk), hogy a
    // rendelésből egyértelmű legyen a döntés — nem csak akkor, ha be volt kapcsolva.
    if (state.linesAvailable) {
      fd.append('properties[Folyók]', toggleOn('rivers') ? 'igen' : 'nem');
      fd.append('properties[Utak]', toggleOn('roads') ? 'igen' : 'nem');
      if (toggleOn('roads')) fd.append('properties[_Utak színe]', roadColorLabel());
    }

    // "_" előtag → a vásárló NEM látja (kosár/pénztár), de az Adminban és az API-ban igen.
    fd.append('properties[_Forrásfájl]', frozenFile);
    if (state.gpxFile) {
      const gz = await maybeGzip(state.gpxFile);
      fd.append('properties[_GPX fájl]', new File([gz.blob], state.gpxFile.name + (gz.gz ? '.gz' : ''), { type: gz.gz ? 'application/gzip' : 'application/gpx+xml' }));
    }

    try {
      const res = await fetch(cfg.routes.cartAdd, { method: 'POST', headers: { Accept: 'application/json' }, body: fd });
      if (!res.ok) { const t = await res.json().catch(() => ({})); throw new Error(t.description || ('HTTP ' + res.status)); }
      setStatus('Kosárba téve. Átirányítás…');
      track_event('tervezo_kosarba', {
        kivitel: state.selectedKivitel || null,
        meret: state.selectedSize || null,
        folyok: toggleOn('rivers'),
        utak: toggleOn('roads'),
        nfc: !nfc.empty,
      });
      window.location.href = cfg.routes.cart;
    } catch (err) { els.addToCart.disabled = false; showOverlay(false); setStatus('Nem sikerült a kosárba rakni: ' + err.message, true); }
  });

  /* ------------------------------------------------------------- UI helpers */
  // A gombok alatti magyarázó szöveg ki van kapcsolva: a státusz csak a konzolra megy (hibakereséshez).
  function setStatus(msg, isError) { if (isError && typeof console !== 'undefined') console.warn('[tervező] ' + msg); }
  function showProgress(on, pct, msg) { els.progressWrap.hidden = !on; if (on) setProgress(pct || 0, msg || ''); }
  function setProgress(pct, msg) { els.progressBar.style.width = pct + '%'; els.progressBar.setAttribute('aria-valuenow', String(pct)); if (msg) els.progressMsg.textContent = msg; }
}

/* ---------------------------------------------------------- misc helpers */
function floatToB64(floatArr) {
  const bytes = new Uint8Array(floatArr.buffer);
  let bin = ''; const CH = 0x8000;
  for (let i = 0; i < bytes.length; i += CH) bin += String.fromCharCode.apply(null, bytes.subarray(i, i + CH));
  return btoa(bin);
}
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

/* ---------------------------------------------------------- Three.js scene */
function createThree(container) {
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
  renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
  container.appendChild(renderer.domElement);
  const scene = new THREE.Scene();
  // A near/far arány dönti el a mélységpuffer pontosságát. A modell ~100 mm, ezért
  // near 0.1 / far 100000 (1:1 000 000) esetén kizoomolva a felszínek "vibrálnak"
  // (z-fighting). Reális értékekkel a pontosság több százszorosára javul.
  const camera = new THREE.PerspectiveCamera(45, 1, 3, 3000);
  const controls = new OrbitControls(camera, renderer.domElement); controls.enableDamping = true;
  controls.minDistance = 60; controls.maxDistance = 600;
  scene.add(new THREE.HemisphereLight(0xffffff, 0x556677, 0.7));
  const dir = new THREE.DirectionalLight(0xffffff, 1.85); dir.position.set(1, 2, 1.5); scene.add(dir);
  const three = { renderer, scene, camera, controls };
  const resize = () => { renderer.setSize(container.clientWidth, container.clientHeight, false); camera.aspect = container.clientWidth / container.clientHeight; camera.updateProjectionMatrix(); };
  three.onResize = resize;
  resize(); new ResizeObserver(resize).observe(container);
  (function loop() { requestAnimationFrame(loop); controls.update(); renderer.render(scene, camera); })();
  return three;
}
function frameObject(three, obj) {
  const box = new THREE.Box3().setFromObject(obj), size = box.getSize(new THREE.Vector3()), center = box.getCenter(new THREE.Vector3());
  obj.position.sub(center);
  const maxDim = Math.max(size.x, size.y, size.z);
  const dist = maxDim / (2 * Math.tan((three.camera.fov * Math.PI / 180) / 2)) * 1.7;
  three.camera.position.set(dist * 0.7, dist * 0.6, dist * 0.9); three.controls.target.set(0, 0, 0); three.controls.update();
}
function disposeGroup(group) { group.traverse((o) => { if (o.geometry) o.geometry.dispose(); if (o.material) o.material.dispose(); }); }
